import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { AuthModal } from "@/components/AuthModal";

const HERO_IMG = "https://images.unsplash.com/photo-1532751203793-812308a10d8e";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 0.2 + i * 0.12 } }),
};

const CATEGORIES = [
  { key: "art", label: "Art", img: "https://images.unsplash.com/flagged/photo-1567934150921-7632371abb32" },
  { key: "cars", label: "Motorcars", img: "https://images.unsplash.com/photo-1503376780353-7e6692767b70" },
  { key: "jewellery", label: "Jewellery", img: "https://images.unsplash.com/photo-1679466061812-211a6b737175" },
  { key: "fashion", label: "Couture", img: "https://images.unsplash.com/photo-1571924848943-25c2c95bbb4b" },
];

const MANIFESTO = [
  { n: "01", t: "Never twice", b: "Every object on 1of1 exists a single time. No editions, no reissues, no second chances. When it is gone, it is gone." },
  { n: "02", t: "Verified singular", b: "Each piece is authenticated and its provenance documented before it reaches the register. Rarity you can stand behind." },
  { n: "03", t: "For the few", b: "A quiet marketplace built for collectors who measure value not in quantity, but in the impossibility of repetition." },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 4)));
  }, []);

  return (
    <div className="bg-[#050505]">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] w-full overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="1of1" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

        <div className="relative z-10 flex h-full flex-col justify-end px-6 pb-20 md:px-12 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 1 }}
            className="mb-6 text-[11px] uppercase tracking-[0.5em] text-gold"
          >
            The marketplace of the singular
          </motion.p>
          <h1 className="font-serif-lux max-w-5xl text-[clamp(3rem,9vw,7rem)] font-light leading-[0.92] tracking-tight text-[#f5f5f0]">
            {["Own the only", "one that exists."].map((t, i) => (
              <span key={i} className="mask-line">
                <motion.span variants={line} initial="hidden" animate="show" custom={i} className="block">
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
          >
            <Link
              to="/marketplace"
              data-testid="hero-explore-btn"
              className="hover-invert group flex items-center gap-3 rounded-none border border-[#f5f5f0] bg-[#f5f5f0] px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0]"
            >
              Enter the collection
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-register-btn"
              onClick={() => setAuthOpen(true)}
              className="text-[11px] uppercase tracking-[0.3em] text-[#a1a1aa] underline underline-offset-8 hover:text-[#f5f5f0]"
            >
              Request access
            </button>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-[#050505] py-8">
        <Marquee speed={30} gradient={false} autoFill>
          {["Art", "Motorcars", "Haute Couture", "Rare Jewellery", "Timepieces", "One of One"].map((w, i) => (
            <span key={i} className="font-serif-lux mx-10 text-5xl font-light italic text-[#1c1c1f] md:text-7xl">
              {w} <span className="not-italic text-gold/40">·</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* FEATURED */}
      <section className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1600px]">
          <div className="mb-16 flex items-end justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Currently on the register</p>
              <h2 className="font-serif-lux mt-4 text-4xl font-light text-[#f5f5f0] md:text-6xl">
                Featured pieces
              </h2>
            </div>
            <Link
              to="/marketplace"
              data-testid="featured-viewall"
              className="hidden text-[11px] uppercase tracking-[0.3em] text-[#a1a1aa] hover:text-[#f5f5f0] md:block"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} tall />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="px-6 pb-24 md:px-12 md:pb-32">
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-4 lg:grid-cols-4">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.key}
              to={`/marketplace?category=${c.key}`}
              data-testid={`category-${c.key}`}
              className="group relative aspect-[3/4] overflow-hidden bg-[#0a0a0a]"
            >
              <img src={c.img} alt={c.label} className="img-zoom h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-100" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">0{i + 1}</span>
                <h3 className="font-serif-lux text-2xl font-light text-[#f5f5f0] md:text-3xl">{c.label}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="border-t border-white/10 bg-[#0a0a0a] px-6 py-24 md:px-12 md:py-40">
        <div className="mx-auto max-w-[1600px]">
          <p className="mb-20 text-[11px] uppercase tracking-[0.4em] text-gold">The Manifesto</p>
          <div className="space-y-24">
            {MANIFESTO.map((m, i) => (
              <motion.div
                key={m.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                className="grid grid-cols-1 items-end gap-6 border-t border-white/10 pt-10 md:grid-cols-12"
              >
                <div className="md:col-span-4">
                  <span className="font-serif-lux text-8xl font-light leading-none text-[#f5f5f0] md:text-[10rem]">
                    {m.n}
                  </span>
                </div>
                <div className="md:col-span-8 md:pl-12">
                  <h3 className="font-serif-lux text-3xl font-light text-[#f5f5f0] md:text-5xl">{m.t}</h3>
                  <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-[#a1a1aa]">{m.b}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTER CTA */}
      <section id="register" className="relative overflow-hidden px-6 py-32 md:px-12 md:py-44">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Join the register</p>
            <h2 className="font-serif-lux mt-6 text-5xl font-light leading-[0.95] text-[#f5f5f0] md:text-7xl">
              Acquire what cannot<br />be acquired again.
            </h2>
            <p className="mx-auto mt-8 max-w-lg text-base font-light leading-relaxed text-[#a1a1aa]">
              Membership grants access to the full collection, private acquisition and the
              ability to consign your own one-of-one pieces.
            </p>
            <button
              data-testid="cta-register-btn"
              onClick={() => setAuthOpen(true)}
              className="hover-invert mt-12 rounded-none border border-[#f5f5f0] bg-[#f5f5f0] px-12 py-5 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0]"
            >
              Request access
            </button>
          </motion.div>
        </div>
      </section>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" />
    </div>
  );
}
